import { clientMoveIn } from "../../lib/clientMoveIn";
// PKT-0306 · "Your Clients" ranked next-action scorer (v1)
//
// Pure, deterministic ranking used by the dashboard "Your Clients" widget
// (replaces "Tasks Due Today"). Answers "who needs the operator RIGHT NOW".
// Mirrors the New Leads widget's shape: one row per client, ranked, with ONE
// recommended action. No I/O — the widget fetches signals (RLS-scoped) and
// hands them here; every function below is unit-testable from fixtures.
//
// Design rules (from the packet):
//  - Deterministic weighted sum, highest first.
//  - Missing signal contributes 0 and NEVER blocks a row (graceful degrade).
//  - Overdue cadence tasks collapse into ONE "pending follow-up" signal —
//    never a wall of per-task rows.
//  - Closed/out-of-contact clients drop off the list entirely.
//
// Signal availability at build time (verified 2026-07-18, see EVENT-0865):
//  PRESENT  contact staleness (clients.last_contacted_at), stage
//           (clients.status/priority), budget (clients.max_budget),
//           cadence collapse (client_tasks pending count)
//  PARTIAL  shortlist engagement (client_shortlists / money_moments events)
//  DEGRADED move-in proximity (clients.move_in_timeline is a TEXT bucket, not
//           a date — coarse scoring only)
//  ABSENT   unanswered inbound (no client-level inbound timestamp today) —
//           wired here so it dominates the instant the data exists, but it
//           degrades to 0 in production until then.

// ---------------------------------------------------------------------------
// Weights — the single tunable block. Ordered by the packet's priority:
// move-in proximity > contact staleness > shortlist > stage > budget, with
// overdue cadence folded in as one mid-weight signal.
// Each contributor returns a normalized 0..1 factor multiplied by its weight,
// so the max theoretical score is the sum of the weights below.
// ---------------------------------------------------------------------------
export const WEIGHTS = {
  moveInProximity: 60,
  contactStaleness: 40,
  pendingFollowup: 30, // ← the collapsed cadence signal (NOT per-task)
  shortlistEngagement: 25,
  stage: 15,
  budget: 10, // tiebreaker only
} as const;

// Tunables for the normalization curves.
const STALE_FULL_DAYS = 14; // days-since-contact that saturates the staleness factor
const MOVEIN_URGENT_DAYS = 7; // ≤ this many days out ⇒ full move-in urgency
const MOVEIN_HORIZON_DAYS = 60; // beyond this ⇒ ~0 move-in urgency
const SHORTLIST_DECAY_DAYS = 7; // shortlist engagement fades to 0 over this window
const PENDING_CAP = 3; // pending-task count saturates here (anti red-wall)
const BUDGET_FULL = 4000; // monthly budget that saturates the tiebreaker factor

// Client stages that should never appear in the widget (deal done / dormant).
// Exported (PKT-0581) so clientQueue.ts's needs_action view drops the same
// set — its header contract says "clients the scorer drops stay dropped here".
export const DROPPED_STATUSES = new Set(["out_of_contact", "closed"]);

// Rough stage ranking from clients.status (+ priority). Higher = more active.
const STAGE_RANK: Record<string, number> = {
  new_client: 0.35,
  touring: 1,
  applied: 0.75,
  payment_received: 0.2,
};

export interface ClientSignals {
  id: string;
  /** F24 (migration 122) · operable test client: never scored or counted. */
  is_test_client?: boolean | null;
  first_name?: string | null;
  last_name?: string | null;
  status?: string | null;
  priority?: string | null; // 'high' | 'normal' | 'low' (optional nudge)
  max_budget?: number | null;
  move_in_timeline?: string | null; // TEXT bucket, e.g. "0-30 days", "asap"
  move_in_date?: string | null; // ISO date if ever backfilled (else null)
  last_contacted_at?: string | null;
  // Collapsed cadence: count of pending/overdue client_tasks (NOT the tasks).
  pending_task_count?: number | null;
  // PKT-0693 S1 · a promise has a CLOCK, not just a count. `pending_task_count`
  // cannot say whether anything is LATE, so an ordering built on it alone would
  // have to invent urgency. Both are measured server-side in
  // backend/api/routes/client_queue.py::get_client_queue (its "pending tasks"
  // block) from COALESCE(due_at, due_date) over PENDING rows only.
  // next_task_due_at is the EARLIEST such promise, normalized to a single
  // +00:00 offset like every other clock on the row; null means UNKNOWN — there
  // is no task, never "due now".
  next_task_due_at?: string | null;
  // Tasks whose due instant has arrived; the boundary is CLOSED, so a task due
  // at exactly now counts as late.
  overdue_task_count?: number | null;
  // The measured age of the row itself. Until S1 the queue did not carry it, and
  // isGoneCold in frontend/src/components/messages/clientQueue.ts says so in its
  // own comment ("the queue has no created_at to say otherwise", wrapped across
  // two lines there) — which is why a never-contacted client is held at fresh
  // WORK rather than aged.
  created_at?: string | null;
  // Most recent shortlist engagement (view/tap), ISO, if captured.
  last_shortlist_engaged_at?: string | null;
  // Unanswered inbound: last inbound after last outbound. Absent today ⇒ leave
  // both null and this signal degrades to 0.
  last_inbound_at?: string | null;
  last_outbound_at?: string | null;
}

export type WhyNowKind =
  | "unanswered_inbound"
  | "move_in"
  | "never_contacted"
  | "stale"
  | "pending_followup"
  | "shortlist"
  | "stage";

export interface RankedClientRow {
  id: string;
  name: string;
  stage: string;
  budget: number | null;
  score: number;
  whyNow: string;
  whyNowKind: WhyNowKind;
  action: string; // single recommended action label
}

const clampUnit = (n: number): number => Math.max(0, Math.min(1, n));

export function daysBetween(fromIso: string, nowIso: string): number | null {
  const a = new Date(fromIso).getTime();
  const b = new Date(nowIso).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return (b - a) / 86_400_000;
}

// Coarse move-in urgency from the TEXT timeline bucket (schema reality) or a
// precise date if one is ever present. Returns 0..1; unknown ⇒ 0.
export function moveInFactor(s: ClientSignals, nowIso: string): number {
  if (s.move_in_date) {
    const d = daysBetween(nowIso, s.move_in_date);
    if (d === null) return 0;
    if (d <= 0) return 0.5; // past-date decays — don't scream forever
    if (d <= MOVEIN_URGENT_DAYS) return 1;
    if (d >= MOVEIN_HORIZON_DAYS) return 0;
    return clampUnit(1 - (d - MOVEIN_URGENT_DAYS) / (MOVEIN_HORIZON_DAYS - MOVEIN_URGENT_DAYS));
  }
  const t = (s.move_in_timeline || "").toLowerCase();
  if (!t) return 0;
  if (/(asap|immediate|now|0-?30|this month|<\s*1|within a month)/.test(t)) return 1;
  if (/(30-?60|1-?2 month|next month)/.test(t)) return 0.6;
  if (/(60-?90|2-?3 month)/.test(t)) return 0.3;
  if (/(90|3\+|flexible|just looking|browsing)/.test(t)) return 0.1;
  return 0.2; // unknown-but-present bucket
}

export function contactStalenessFactor(s: ClientSignals, nowIso: string): number {
  if (!s.last_contacted_at) return 1; // never contacted ⇒ maximally urgent
  const d = daysBetween(s.last_contacted_at, nowIso);
  if (d === null || d <= 0) return 0;
  return clampUnit(d / STALE_FULL_DAYS);
}

export function shortlistFactor(s: ClientSignals, nowIso: string): number {
  if (!s.last_shortlist_engaged_at) return 0;
  const d = daysBetween(s.last_shortlist_engaged_at, nowIso);
  if (d === null || d < 0) return 0;
  return clampUnit(1 - d / SHORTLIST_DECAY_DAYS);
}

export function pendingFollowupFactor(s: ClientSignals): number {
  const n = s.pending_task_count ?? 0;
  if (n <= 0) return 0;
  return clampUnit(n / PENDING_CAP); // collapsed: one signal, count-capped
}

export function stageFactor(s: ClientSignals): number {
  const base = STAGE_RANK[(s.status || "").toLowerCase()] ?? 0.35;
  const bump = (s.priority || "").toLowerCase() === "high" ? 0.15 : 0;
  return clampUnit(base + bump);
}

export function budgetFactor(s: ClientSignals): number {
  const b = s.max_budget ?? 0;
  if (b <= 0) return 0;
  return clampUnit(b / BUDGET_FULL);
}

export function clientDisplayName(s: ClientSignals): string {
  return [s.first_name, s.last_name].filter(Boolean).join(" ").trim() || "Client";
}

interface Contribution {
  score: number;
  factor: number;
}

// The recommended single action + the "why now" line, chosen from the highest
// non-zero contributor so the row reads as one clear next step.
function buildWhyNow(
  s: ClientSignals,
  nowIso: string,
  contrib: Record<string, Contribution>,
): { whyNow: string; whyNowKind: WhyNowKind; action: string } {
  const name = clientDisplayName(s).split(" ")[0] || "client";
  // Priority order for the human line mirrors the weight order.
  const order: WhyNowKind[] = [
    "move_in",
    "never_contacted",
    "stale",
    "pending_followup",
    "shortlist",
    "stage",
  ];
  const active = new Set<WhyNowKind>();
  if (contrib.moveInProximity.factor > 0) active.add("move_in");
  if (contrib.contactStaleness.factor > 0) {
    active.add(s.last_contacted_at ? "stale" : "never_contacted");
  }
  if (contrib.pendingFollowup.factor > 0) active.add("pending_followup");
  if (contrib.shortlistEngagement.factor > 0) active.add("shortlist");
  active.add("stage");

  const kind = order.find((k) => active.has(k)) ?? "stage";
  const staleDays = s.last_contacted_at ? Math.floor(daysBetween(s.last_contacted_at, nowIso) ?? 0) : null;
  const pending = s.pending_task_count ?? 0;

  switch (kind) {
    case "move_in":
      return {
        whyNow: clientMoveIn(s) ? `moving in · ${clientMoveIn(s)}` : "move-in approaching",
        whyNowKind: kind,
        action: `Open ${name}`,
      };
    case "never_contacted":
      return { whyNow: "no contact logged yet", whyNowKind: kind, action: `Reach out to ${name}` };
    case "stale":
      return {
        whyNow: `no contact in ${staleDays} day${staleDays === 1 ? "" : "s"}`,
        whyNowKind: kind,
        action: `Follow up with ${name}`,
      };
    case "pending_followup":
      return {
        whyNow: `${pending} follow-up${pending === 1 ? "" : "s"} pending`,
        whyNowKind: kind,
        action: `Open ${name}`,
      };
    case "shortlist":
      return { whyNow: "opened their shortlist recently", whyNowKind: kind, action: `Open ${name}` };
    default:
      return {
        whyNow: (s.status || "client").toLowerCase(),
        whyNowKind: "stage",
        action: `Open ${name}`,
      };
  }
}

export function scoreClient(s: ClientSignals, nowIso: string): RankedClientRow {
  const contrib: Record<string, Contribution> = {
    moveInProximity: mul(moveInFactor(s, nowIso), WEIGHTS.moveInProximity),
    contactStaleness: mul(contactStalenessFactor(s, nowIso), WEIGHTS.contactStaleness),
    pendingFollowup: mul(pendingFollowupFactor(s), WEIGHTS.pendingFollowup),
    shortlistEngagement: mul(shortlistFactor(s, nowIso), WEIGHTS.shortlistEngagement),
    stage: mul(stageFactor(s), WEIGHTS.stage),
    // Grant's revenue-first ruling: among never-contacted qualified clients,
    // budget is the primary ordering signal rather than a minor tiebreaker.
    budget: mul(budgetFactor(s), s.last_contacted_at ? WEIGHTS.budget : 100),
  };
  const score = Object.values(contrib).reduce((sum, c) => sum + c.score, 0);
  const { whyNow, whyNowKind, action } = buildWhyNow(s, nowIso, contrib);
  return {
    id: s.id,
    name: clientDisplayName(s),
    stage: (s.status || "").toLowerCase() || "client",
    budget: s.max_budget ?? null,
    score: Math.round(score * 100) / 100,
    whyNow,
    whyNowKind,
    action,
  };
}

function mul(factor: number, weight: number): Contribution {
  const f = clampUnit(factor);
  return { factor: f, score: f * weight };
}

/**
 * Rank a set of clients by "who needs the operator right now". Drops
 * placed/inactive clients. Deterministic: ties break by score, then name, then
 * id — never by array order, so the output is stable across renders. Does not
 * mutate the input.
 */
export function selectRankedClients(
  clients: ReadonlyArray<ClientSignals>,
  nowIso: string,
): RankedClientRow[] {
  return clients
    .filter((c) => !DROPPED_STATUSES.has((c.status || "").toLowerCase()))
    .filter((c) => c.is_test_client !== true) // F24 · never ranked
    .map((c) => scoreClient(c, nowIso))
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.name.localeCompare(b.name) ||
        a.id.localeCompare(b.id),
    );
}
